export function parseGraphTimeToSeconds(time) {
  if (time == null) return NaN;
  if (typeof time === "number" && Number.isFinite(time)) return time;
  const s = String(time).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return NaN;
}

/** SVG coordinate width for the HR curve (fills PC dialogs better than the old 300). */
export const HR_CURVE_VIEW_WIDTH = 650;
export const HR_CURVE_PLOT_LEFT = 32;
export const HR_CURVE_PLOT_WIDTH = 608;
export const HR_CURVE_VIEW_HEIGHT = 260;
export const HR_CURVE_PLOT_TOP = 16;
export const HR_CURVE_PLOT_BOTTOM = 226;

/** Desktop dashboard: wide, shallow plot so the curve stays on-screen beside zones. */
export const HR_CURVE_DESKTOP_VIEW_WIDTH = 760;
export const HR_CURVE_DESKTOP_PLOT_LEFT = 36;
export const HR_CURVE_DESKTOP_PLOT_WIDTH = 714;
export const HR_CURVE_DESKTOP_VIEW_HEIGHT = 196;
export const HR_CURVE_DESKTOP_PLOT_TOP = 10;
export const HR_CURVE_DESKTOP_PLOT_BOTTOM = 168;

/** Mobile coordinates: taller portrait aspect ratio so chart dominates mobile screen height */
export const HR_CURVE_MOBILE_VIEW_WIDTH = 360;
export const HR_CURVE_MOBILE_PLOT_LEFT = 28;
export const HR_CURVE_MOBILE_PLOT_WIDTH = 324;
export const HR_CURVE_MOBILE_VIEW_HEIGHT = 320;
export const HR_CURVE_MOBILE_PLOT_TOP = 16;
export const HR_CURVE_MOBILE_PLOT_BOTTOM = 286;

export function readingChartTimeSec(row, anchorMs) {
  // Prefer wall-clock when available so multi-exercise session series are not
  // collapsed onto exercise-relative recorded_sec (often 0..~25m).
  if (Number.isFinite(anchorMs) && row.recorded_at) {
    const t = new Date(row.recorded_at).getTime();
    if (Number.isFinite(t)) return Math.max(0, Math.round((t - anchorMs) / 1000));
  }
  const rs = Number(row.recorded_sec);
  if (Number.isFinite(rs) && rs >= 0) return rs;
  return 0;
}

export function parseWallMs(value) {
  if (value == null || value === "") return NaN;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : NaN;
}

/**
 * Build evenly spaced wall-clock ticks (3 or 5), always including endpoints.
 */
export function buildWallClockTimeTicks(startMs, endMs, preferredCount = 5) {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
    return [];
  }
  const span = endMs - startMs;
  if (span <= 0) {
    return [{ ms: startMs, ratio: 0 }];
  }
  const count = preferredCount === 3 ? 3 : 5;
  const ticks = [];
  for (let i = 0; i < count; i++) {
    const ratio = i / (count - 1);
    ticks.push({
      ms: startMs + span * ratio,
      ratio,
    });
  }
  return ticks;
}

export function formatClockTick(ms, localeCode, { withSeconds = false } = {}) {
  if (!Number.isFinite(ms)) return "";
  try {
    return new Intl.DateTimeFormat(localeCode || undefined, {
      hour: "2-digit",
      minute: "2-digit",
      ...(withSeconds ? { second: "2-digit" } : {}),
    }).format(new Date(ms));
  } catch {
    const d = new Date(ms);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    if (!withSeconds) return `${hh}:${mm}`;
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }
}

export function resolveChartTimeDomain(hrInfo, points = []) {
  const startCandidates = [
    hrInfo?.chart_window_start_at,
    hrInfo?.te_start_time,
    hrInfo?.session_window_start_at,
    hrInfo?.summary_window_start_at,
    hrInfo?.involvement_window_start_at,
    hrInfo?.hr_range_start_at,
  ];
  const endCandidates = [
    hrInfo?.chart_window_end_at,
    hrInfo?.te_end_time,
    hrInfo?.session_window_end_at,
    hrInfo?.summary_window_end_at,
    hrInfo?.involvement_window_end_at,
    hrInfo?.hr_range_end_at,
  ];

  // Use earliest start / latest end across metadata so a short chart_window
  // (e.g. first exercise only) cannot truncate a longer session window.
  let startMs = NaN;
  for (const c of startCandidates) {
    const t = parseWallMs(c);
    if (!Number.isFinite(t)) continue;
    if (!Number.isFinite(startMs) || t < startMs) startMs = t;
  }
  let endMs = NaN;
  for (const c of endCandidates) {
    const t = parseWallMs(c);
    if (!Number.isFinite(t)) continue;
    if (!Number.isFinite(endMs) || t > endMs) endMs = t;
  }

  if ((!Number.isFinite(startMs) || !Number.isFinite(endMs)) && points.length) {
    const times = points
      .map((p) => parseWallMs(p.recorded_at))
      .filter((t) => Number.isFinite(t));
    if (times.length) {
      if (!Number.isFinite(startMs)) startMs = Math.min(...times);
      if (!Number.isFinite(endMs)) endMs = Math.max(...times);
    }
  }

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    const secTimes = points.map((p) => p.time).filter((t) => Number.isFinite(t));
    if (secTimes.length) {
      const tMin = Math.min(...secTimes);
      const tMax = Math.max(...secTimes);
      return {
        startMs: NaN,
        endMs: NaN,
        tMinSec: tMin,
        tMaxSec: tMax,
        anchorMs: NaN,
        useWallClock: false,
      };
    }
    return {
      startMs: NaN,
      endMs: NaN,
      tMinSec: 0,
      tMaxSec: 0,
      anchorMs: NaN,
      useWallClock: false,
    };
  }

  if (endMs < startMs) {
    const tmp = startMs;
    startMs = endMs;
    endMs = tmp;
  }

  // Extend domain to cover sample span when window metadata is short
  // (e.g. only first exercise end was used for chart_window_end_at).
  if (points.length) {
    const sampleTimes = points
      .map((p) => parseWallMs(p.recorded_at))
      .filter((t) => Number.isFinite(t));
    if (sampleTimes.length) {
      const sampleEnd = Math.max(...sampleTimes);
      if (sampleEnd > endMs) endMs = sampleEnd;
    } else {
      const secTimes = points.map((p) => p.time).filter((t) => Number.isFinite(t));
      if (secTimes.length) {
        const sampleMaxSec = Math.max(...secTimes);
        const domainSec = Math.round((endMs - startMs) / 1000);
        if (sampleMaxSec > domainSec) {
          endMs = startMs + sampleMaxSec * 1000;
        }
      }
    }
  }

  return {
    startMs,
    endMs,
    tMinSec: 0,
    tMaxSec: Math.max(0, Math.round((endMs - startMs) / 1000)),
    anchorMs: startMs,
    useWallClock: true,
  };
}

export function normalizeSubjectRoleKind(roleOrKind) {
  if (!roleOrKind) return null;
  const r = String(roleOrKind).toLowerCase().trim();
  if (r === "active" || r === "both" || r.includes("actif") || r.includes("activo") || r.includes("aktiv")) {
    return "active";
  }
  if (r === "sparring" || r.includes("spar")) return "sparring";
  if (r === "other") return "other";
  return null;
}

export function resolveSubjectRoleKindFromWindow(w) {
  if (!w || typeof w !== "object") return null;
  // Prefer explicit subject role (pair match or roster-position fallback).
  const fromKind =
    normalizeSubjectRoleKind(w.subject_role_kind) ||
    normalizeSubjectRoleKind(w.subject_role);
  if (fromKind === "active" || fromKind === "sparring") return fromKind;
  // Off-pair / unknown subject with no explicit role — do not invent sparring
  // via strict_active (that previously mis-labeled multi-table windows).
  if (w.roster_off_pair === true) return null;
  if (
    (w.subject_role_kind === null || w.subject_role_kind === undefined) &&
    (w.subject_role === null || w.subject_role === undefined) &&
    (w.role_a != null || w.role_b != null)
  ) {
    return null;
  }
  // Legacy payloads without subject_role fields.
  if (typeof w.strict_active === "boolean") {
    return w.strict_active ? "active" : "sparring";
  }
  return null;
}

/**
 * Build Active/Sparring highlight windows from the sequences the UI already
 * resolved for this viewer (roster-position roles + tes_start/end).
 * Used when HR `sequence_windows_used` is empty or lacks subject roles.
 *
 * @param {Array<{ seq?: object, roleVariant?: string, role?: string }>} entries
 * @param {{ tMaxSec?: number, anchorMs?: number, startMs?: number }|null} domain
 * @param {object|null} hrInfo
 */
export function extractRoleWindowsFromPerformedSequences(
  entries = [],
  domain = null,
  hrInfo = null
) {
  if (!Array.isArray(entries) || !entries.length) return [];

  let anchorMs = NaN;
  const preferredAnchors = [
    hrInfo?.te_start_time,
    hrInfo?.chart_window_start_at,
    domain?.anchorMs,
    domain?.startMs,
  ];
  for (const c of preferredAnchors) {
    const t = typeof c === "number" ? c : parseWallMs(c);
    if (Number.isFinite(t)) {
      anchorMs = t;
      break;
    }
  }
  if (!Number.isFinite(anchorMs)) {
    const starts = entries
      .map((e) => parseWallMs(e?.seq?.tes_start_time))
      .filter((t) => Number.isFinite(t));
    if (starts.length) anchorMs = Math.min(...starts);
  }

  const tMax = Number.isFinite(domain?.tMaxSec) ? domain.tMaxSec : NaN;
  const hasWallAnchor = Number.isFinite(anchorMs);
  let cursorSec = 0;
  const out = [];

  for (const entry of entries) {
    const kind =
      normalizeSubjectRoleKind(entry?.roleVariant) ||
      normalizeSubjectRoleKind(entry?.role);
    if (kind !== "active" && kind !== "sparring") continue;

    const seq = entry?.seq || entry;
    const wall0 = parseWallMs(seq?.tes_start_time);
    const wall1 = parseWallMs(seq?.tes_end_time);

    let t0 = NaN;
    let t1 = NaN;
    if (hasWallAnchor && Number.isFinite(wall0) && Number.isFinite(wall1) && wall1 > wall0) {
      t0 = Math.max(0, (wall0 - anchorMs) / 1000);
      t1 = Math.max(t0, (wall1 - anchorMs) / 1000);
    } else {
      // No wall times: stack planned sequence_duration along the chart timeline.
      const minutes = Number(seq?.sequence_duration);
      const durSec =
        Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes * 60) : 0;
      if (!(durSec > 0)) continue;
      t0 = cursorSec;
      t1 = cursorSec + durSec;
      cursorSec = t1;
    }

    if (Number.isFinite(tMax) && tMax > 0) {
      t0 = Math.min(t0, tMax);
      t1 = Math.min(t1, tMax);
    }
    if (!(t1 > t0)) continue;

    out.push({
      t0,
      t1,
      role: kind,
      strictActive: kind === "active",
      tes_id: seq?.tes_id != null ? String(seq.tes_id) : null,
      sequence_order:
        seq?.sequence_order != null && Number.isFinite(Number(seq.sequence_order))
          ? Number(seq.sequence_order)
          : null,
      fromPerformed: true,
    });
  }

  return out;
}

/** Merge API windows with performed-sequence fallback; prefer API when both exist. */
export function mergeHrRoleWindows(apiWindows = [], performedWindows = []) {
  const out = [];
  const seen = new Set();
  const keyOf = (w) => {
    if (w?.tes_id) return `tes:${w.tes_id}`;
    if (w?.sequence_order != null) return `ord:${w.sequence_order}`;
    return `t:${w?.t0}-${w?.t1}-${w?.role}`;
  };
  for (const w of apiWindows || []) {
    if (!w) continue;
    out.push(w);
    seen.add(keyOf(w));
  }
  for (const w of performedWindows || []) {
    if (!w) continue;
    const k = keyOf(w);
    if (seen.has(k)) continue;
    out.push(w);
    seen.add(k);
  }
  return out;
}

export function extractRoleWindows(hrInfo, domain) {
  const windows = Array.isArray(hrInfo?.sequence_windows_used)
    ? hrInfo.sequence_windows_used
    : [];
  if (!windows.length) return [];

  // Prefer the same wall-clock anchor the chart uses for readings (te / chart window).
  let anchorMs = NaN;
  const preferredAnchors = [
    hrInfo?.te_start_time,
    hrInfo?.chart_window_start_at,
    domain?.anchorMs,
    domain?.startMs,
  ];
  for (const c of preferredAnchors) {
    const t = typeof c === "number" ? c : parseWallMs(c);
    if (Number.isFinite(t)) {
      anchorMs = t;
      break;
    }
  }
  if (!Number.isFinite(anchorMs)) {
    const starts = windows
      .map(
        (w) =>
          parseWallMs(w.role_wall_start_at) || parseWallMs(w.wall_start_at)
      )
      .filter((t) => Number.isFinite(t));
    if (starts.length) anchorMs = Math.min(...starts);
  }
  if (!Number.isFinite(anchorMs)) return [];

  const tMax = Number.isFinite(domain?.tMaxSec) ? domain.tMaxSec : NaN;

  return windows
    .map((w) => {
      const kind = resolveSubjectRoleKindFromWindow(w);
      if (kind !== "active" && kind !== "sparring") return null;

      // Prefer true sequence bounds for highlight (not head-block pre-roll).
      const wall0 =
        parseWallMs(w.role_wall_start_at) || parseWallMs(w.wall_start_at);
      const wall1 =
        parseWallMs(w.role_wall_end_at) || parseWallMs(w.wall_end_at);
      if (!Number.isFinite(wall0) || !Number.isFinite(wall1)) return null;
      let t0 = Math.max(0, (wall0 - anchorMs) / 1000);
      let t1 = Math.max(t0, (wall1 - anchorMs) / 1000);
      if (Number.isFinite(tMax) && tMax > 0) {
        t0 = Math.min(t0, tMax);
        t1 = Math.min(t1, tMax);
      }
      if (!(t1 > t0)) return null;
      return {
        t0,
        t1,
        role: kind,
        strictActive: !!w.strict_active,
        tes_id: w.tes_id != null ? String(w.tes_id) : null,
        sequence_order:
          w.sequence_order != null && Number.isFinite(Number(w.sequence_order))
            ? Number(w.sequence_order)
            : null,
      };
    })
    .filter(Boolean);
}

/** Roles present on the HR payload (even if a window failed time-mapping). */
export function extractAvailableHrRoles(hrInfo, roleWindows = []) {
  const set = new Set();
  for (const w of roleWindows || []) {
    if (w?.role === "active" || w?.role === "sparring") set.add(w.role);
  }
  const raw = Array.isArray(hrInfo?.sequence_windows_used)
    ? hrInfo.sequence_windows_used
    : [];
  for (const w of raw) {
    const kind = resolveSubjectRoleKindFromWindow(w);
    if (kind === "active" || kind === "sparring") set.add(kind);
  }
  return set;
}

/** Match a chart role window to a sequence selection (tes_id preferred, else order). */
export function roleWindowMatchesSequence(window, selected = null) {
  if (!window || !selected) return false;
  const selectedTesId =
    selected.tesId != null && String(selected.tesId).trim() !== ""
      ? String(selected.tesId)
      : null;
  const selectedOrder =
    selected.sequenceOrder != null &&
    Number.isFinite(Number(selected.sequenceOrder))
      ? Number(selected.sequenceOrder)
      : null;

  if (selectedTesId && window.tes_id && String(window.tes_id) === selectedTesId) {
    return true;
  }
  if (
    selectedOrder != null &&
    window.sequence_order != null &&
    Number(window.sequence_order) === selectedOrder
  ) {
    return true;
  }
  return false;
}

export function sequenceHasHrWindow(seq, hrInfo) {
  const windows = Array.isArray(hrInfo?.sequence_windows_used)
    ? hrInfo.sequence_windows_used
    : [];
  if (!windows.length || !seq) return false;
  const tesId = seq.tes_id != null ? String(seq.tes_id) : null;
  const isSyntheticOnce =
    tesId && (tesId.startsWith("once-") || !Number.isFinite(Number(tesId)));
  if (isSyntheticOnce) {
    return windows.length > 0;
  }
  const order =
    seq.sequence_order != null && Number.isFinite(Number(seq.sequence_order))
      ? Number(seq.sequence_order)
      : null;
  return windows.some((w) =>
    roleWindowMatchesSequence(
      {
        tes_id: w.tes_id != null ? String(w.tes_id) : null,
        sequence_order:
          w.sequence_order != null && Number.isFinite(Number(w.sequence_order))
            ? Number(w.sequence_order)
            : null,
      },
      { tesId, sequenceOrder: order }
    )
  );
}

export function buildHrSeries(info) {
  if (!info) return { points: [], durationSec: 0 };

  const domain = resolveChartTimeDomain(info, []);
  // Match extractRoleWindows: prefer exercise/chart start so role bands line up.
  const preferredAnchor =
    parseWallMs(info?.te_start_time) ||
    parseWallMs(info?.chart_window_start_at) ||
    (Number.isFinite(domain.anchorMs) ? domain.anchorMs : NaN);
  const anchorMs = Number.isFinite(preferredAnchor) ? preferredAnchor : domain.anchorMs;

  const readings = info.readings;
  if (Array.isArray(readings) && readings.length > 0) {
    const sampleAnchor =
      Number.isFinite(anchorMs)
        ? anchorMs
        : readings[0]?.recorded_at
          ? new Date(readings[0].recorded_at).getTime()
          : NaN;
    const points = readings
      .map((d) => {
        const time = readingChartTimeSec(d, sampleAnchor);
        return {
          time,
          hr_value: Number(d.hr_value) || 0,
          recorded_at: d.recorded_at || null,
        };
      })
      .filter((d) => d.hr_value > 0 && Number.isFinite(d.time))
      .sort((a, b) => {
        const ta = parseWallMs(a.recorded_at);
        const tb = parseWallMs(b.recorded_at);
        if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
        return a.time - b.time;
      });
    const times = points.map((p) => p.time);
    const durationSec =
      times.length >= 2 ? Math.max(...times) - Math.min(...times) : 0;
    const resolvedDomain = resolveChartTimeDomain(info, points);
    // Keep chart domain anchor aligned with role-window mapping.
    if (Number.isFinite(sampleAnchor)) {
      resolvedDomain.anchorMs = sampleAnchor;
      if (!Number.isFinite(resolvedDomain.startMs)) {
        resolvedDomain.startMs = sampleAnchor;
      }
    }
    return { points, durationSec, domain: resolvedDomain };
  }

  const graph = info.hrGraph;
  if (Array.isArray(graph) && graph.length > 0) {
    const points = [];
    for (const d of graph) {
      const t = parseGraphTimeToSeconds(d.time);
      const hr = Number(d.hr_value) || 0;
      if (!Number.isFinite(t) || t < 0 || hr <= 0) continue;
      points.push({ time: t, hr_value: hr, recorded_at: null });
    }
    points.sort((a, b) => a.time - b.time);
    const durationSec =
      points.length > 1
        ? points[points.length - 1].time - points[0].time
        : 0;
    return { points, durationSec, domain: resolveChartTimeDomain(info, points) };
  }

  return { points: [], durationSec: 0, domain: resolveChartTimeDomain(info, []) };
}

/**
 * Peak-preserving downsample matching iOS SessionResultsVC / ExerciseDetailsDialogVC
 * (even index sampling + inject true max BPM into nearest sample).
 */
export function downsampleAndSmoothPoints(points, targetCount = 30) {
  if (!points || points.length === 0) return [];

  const sorted = [...points].sort((a, b) => a.time - b.time);
  const filtered = sorted.filter((p) => p.hr_value >= 35 && p.hr_value <= 230);
  if (filtered.length === 0) return [];
  if (filtered.length <= targetCount) return filtered;

  const result = [];
  const step = (filtered.length - 1) / (targetCount - 1);
  for (let i = 0; i < targetCount; i++) {
    const index = Math.round(i * step);
    const clampedIndex = Math.max(0, Math.min(filtered.length - 1, index));
    result.push(filtered[clampedIndex]);
  }

  // Peak-preserving downsampling (same as iOS)
  let maxReading = filtered[0];
  let originalMaxIdx = 0;
  for (let i = 1; i < filtered.length; i++) {
    if (filtered[i].hr_value > maxReading.hr_value) {
      maxReading = filtered[i];
      originalMaxIdx = i;
    }
  }
  let closestDownsampledIdx = 0;
  let minDiff = Infinity;
  for (let i = 0; i < targetCount; i++) {
    const index = Math.round(i * step);
    const diff = Math.abs(index - originalMaxIdx);
    if (diff < minDiff) {
      minDiff = diff;
      closestDownsampledIdx = i;
    }
  }
  if (closestDownsampledIdx < result.length) {
    result[closestDownsampledIdx] = maxReading;
  }
  return result;
}

/** Catmull-Rom-style cubic Bezier with tension 0.15 — matches iOS HR training summary chart. */
export function getCurvePath(points) {
  if (points.length === 0) return "";
  if (points.length === 1) {
    return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  }

  const tension = 0.15;
  let path = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const p0 = i > 0 ? points[i - 1] : p1;
    const p3 = i < points.length - 2 ? points[i + 2] : p2;

    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;

    path += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return path;
}

function pointInRoleWindows(timeSec, windows, role, selectedSequence = null) {
  if (!windows?.length) return false;
  for (const w of windows) {
    if (selectedSequence) {
      if (!roleWindowMatchesSequence(w, selectedSequence)) continue;
    } else if (role && w.role !== role) {
      continue;
    }
    if (timeSec >= w.t0 && timeSec <= w.t1) return true;
  }
  return false;
}

function lerpHrPoint(a, b, timeSec, mapX, yMinBpm, yMaxBpm, plotTop, plotBottom, plotHeight) {
  if (!a || !b || !(b.time > a.time)) return null;
  if (timeSec < a.time || timeSec > b.time) return null;
  const t = (timeSec - a.time) / (b.time - a.time);
  const hr = a.hr_value + (b.hr_value - a.hr_value) * t;
  let yVal = plotBottom - ((hr - yMinBpm) / (yMaxBpm - yMinBpm)) * plotHeight;
  yVal = Math.max(plotTop, Math.min(plotBottom, yVal));
  return {
    x: mapX(timeSec),
    y: yVal,
    time: timeSec,
    hr_value: hr,
    highlighted: true,
  };
}

/**
 * Build highlight strokes clipped to role-window edges so the colored curve
 * does not start before (or end after) the vertical highlight bands.
 * @returns {Array<{ path: string, role: string }>}
 */
function buildRoleHighlightPaths(
  mappedPoints,
  roleWindows,
  highlightRole,
  selectedSequence,
  mapX,
  yMinBpm,
  yMaxBpm,
  plotTop,
  plotBottom,
  plotHeight
) {
  if (!mappedPoints.length || !roleWindows?.length) return [];

  const activeWindows = roleWindows.filter((w) => {
    if (selectedSequence) return roleWindowMatchesSequence(w, selectedSequence);
    return !highlightRole || w.role === highlightRole;
  });
  if (!activeWindows.length) return [];

  const flushSegment = (segment, role) => {
    if (!segment.length) return;
    const path = getCurvePath(segment);
    if (path) paths.push({ path, role: role || "active" });
    segment.length = 0;
  };

  const paths = [];
  for (const w of activeWindows) {
    const segment = [];
    const role = w.role || "active";
    for (let i = 0; i < mappedPoints.length; i++) {
      const p = mappedPoints[i];
      const prev = i > 0 ? mappedPoints[i - 1] : null;
      const inside = p.time >= w.t0 && p.time <= w.t1;
      const prevInside = prev ? prev.time >= w.t0 && prev.time <= w.t1 : false;

      // Downsample can jump over a short role window — still draw the stroke.
      if (prev && !prevInside && !inside && prev.time < w.t0 && p.time > w.t1) {
        const enter = lerpHrPoint(
          prev,
          p,
          w.t0,
          mapX,
          yMinBpm,
          yMaxBpm,
          plotTop,
          plotBottom,
          plotHeight
        );
        const leave = lerpHrPoint(
          prev,
          p,
          w.t1,
          mapX,
          yMinBpm,
          yMaxBpm,
          plotTop,
          plotBottom,
          plotHeight
        );
        const bridge = [enter, leave].filter(Boolean);
        if (bridge.length) {
          const path = getCurvePath(bridge);
          if (path) paths.push({ path, role });
        }
        continue;
      }

      if (inside && !prevInside && prev) {
        const enter = lerpHrPoint(
          prev,
          p,
          w.t0,
          mapX,
          yMinBpm,
          yMaxBpm,
          plotTop,
          plotBottom,
          plotHeight
        );
        if (enter) segment.push(enter);
      }

      if (inside) {
        segment.push(p);
      }

      if (!inside && prevInside && prev) {
        const leave = lerpHrPoint(
          prev,
          p,
          w.t1,
          mapX,
          yMinBpm,
          yMaxBpm,
          plotTop,
          plotBottom,
          plotHeight
        );
        if (leave) segment.push(leave);
        flushSegment(segment, role);
      }
    }
    flushSegment(segment, role);
  }
  return paths;
}

export function computeHrCurvePaths(rawPoints, mhr, options = {}) {
  const {
    domain = null,
    roleWindows = [],
    highlightRole = null,
    selectedSequence = null,
    tickCount = 5,
    localeCode = undefined,
  } = options;

  const viewWidth = options?.viewWidth ?? HR_CURVE_VIEW_WIDTH;
  const viewHeight = options?.viewHeight ?? HR_CURVE_VIEW_HEIGHT;
  const plotTop = options?.plotTop ?? HR_CURVE_PLOT_TOP;
  const plotBottom = options?.plotBottom ?? (options?.viewHeight ? options.viewHeight - 34 : HR_CURVE_PLOT_BOTTOM);
  const plotHeight = plotBottom - plotTop;
  const isMobileView = viewWidth === HR_CURVE_MOBILE_VIEW_WIDTH;
  const isDesktopView = viewWidth === HR_CURVE_DESKTOP_VIEW_WIDTH;
  const defaultPlotLeft = isMobileView
    ? HR_CURVE_MOBILE_PLOT_LEFT
    : isDesktopView
      ? HR_CURVE_DESKTOP_PLOT_LEFT
      : HR_CURVE_PLOT_LEFT;
  const defaultPlotWidth = isMobileView
    ? HR_CURVE_MOBILE_PLOT_WIDTH
    : isDesktopView
      ? HR_CURVE_DESKTOP_PLOT_WIDTH
      : HR_CURVE_PLOT_WIDTH;
  const plotLeft = options?.plotLeft ?? defaultPlotLeft;
  const plotWidth = options?.plotWidth ?? defaultPlotWidth;

  const yTicks = [
    { bpm: Math.round(mhr), y: plotTop },
    { bpm: Math.round(mhr * 0.8), y: plotTop + plotHeight * (1 / 3) },
    { bpm: Math.round(mhr * 0.6), y: plotTop + plotHeight * (2 / 3) },
    { bpm: Math.round(mhr * 0.4), y: plotBottom },
  ];

  if (!rawPoints?.length) {
    return {
      areaPath: "",
      linePath: "",
      highlightLinePaths: [],
      maxPoint: { x: plotLeft + plotWidth, y: plotTop },
      xTicks: [],
      yTicks,
      highlightBands: [],
      plotLeft,
      plotWidth,
      viewWidth,
      viewHeight,
      plotTop,
      plotBottom,
      plotHeight,
    };
  }

  // Match iOS session/exercise training summary: fixed 30-point peak-preserving downsample
  const points = downsampleAndSmoothPoints(rawPoints, 30);
  if (points.length === 0) {
    return {
      areaPath: "",
      linePath: "",
      highlightLinePaths: [],
      maxPoint: { x: plotLeft + plotWidth, y: plotTop },
      xTicks: [],
      yTicks,
      highlightBands: [],
      plotLeft,
      plotWidth,
      viewWidth,
      viewHeight,
      plotTop,
      plotBottom,
      plotHeight,
    };
  }

  const sampleTMin = Math.min(...points.map((p) => p.time));
  const sampleTMax = Math.max(...points.map((p) => p.time));
  const tMin =
    domain && Number.isFinite(domain.tMinSec) ? domain.tMinSec : sampleTMin;
  const tMax =
    domain && Number.isFinite(domain.tMaxSec) && domain.tMaxSec > tMin
      ? domain.tMaxSec
      : sampleTMax === sampleTMin
        ? sampleTMin + 1
        : sampleTMax;

  const yMinBpm = mhr * 0.4;
  const yMaxBpm = mhr;

  const mapX = (timeSec) => {
    if (tMax === tMin) return plotLeft + plotWidth / 2;
    return plotLeft + ((timeSec - tMin) / (tMax - tMin)) * plotWidth;
  };

  const hasSequenceSelection = !!(
    selectedSequence?.tesId ||
    (selectedSequence?.sequenceOrder != null &&
      Number.isFinite(Number(selectedSequence.sequenceOrder)))
  );
  // Highlight only when a specific role is selected (or a sequence is focused).
  // "Both" (null highlightRole) keeps the plain red curve + max-HR marker.
  const shouldHighlight = hasSequenceSelection || !!highlightRole;

  const mappedPoints = points.map((p) => {
    const xVal = mapX(p.time);
    let yVal = plotBottom - ((p.hr_value - yMinBpm) / (yMaxBpm - yMinBpm)) * plotHeight;
    yVal = Math.max(plotTop, Math.min(plotBottom, yVal));
    return {
      x: xVal,
      y: yVal,
      time: p.time,
      hr_value: p.hr_value,
      highlighted: shouldHighlight
        ? pointInRoleWindows(
            p.time,
            roleWindows,
            highlightRole,
            hasSequenceSelection ? selectedSequence : null
          )
        : false,
    };
  });

  const linePath = getCurvePath(mappedPoints);
  const areaPath = linePath
    ? `${linePath} L ${mappedPoints[mappedPoints.length - 1].x.toFixed(1)} ${plotBottom} L ${mappedPoints[0].x.toFixed(1)} ${plotBottom} Z`
    : "";

  const highlightLinePaths = shouldHighlight
    ? buildRoleHighlightPaths(
        mappedPoints,
        roleWindows,
        highlightRole,
        hasSequenceSelection ? selectedSequence : null,
        mapX,
        yMinBpm,
        yMaxBpm,
        plotTop,
        plotBottom,
        plotHeight
      )
    : [];

  const highlightBands = shouldHighlight
    ? roleWindows
        .filter((w) => {
          if (hasSequenceSelection) {
            return roleWindowMatchesSequence(w, selectedSequence);
          }
          return !highlightRole || w.role === highlightRole;
        })
        .map((w) => {
          const x0 = mapX(w.t0);
          const x1 = mapX(w.t1);
          return {
            x: Math.min(x0, x1),
            width: Math.max(0, Math.abs(x1 - x0)),
            role: w.role === "sparring" ? "sparring" : "active",
            selected: hasSequenceSelection,
          };
        })
        .filter((b) => b.width > 0.5)
    : [];

  let peakIndex = 0;
  let peakMaxBpm = -1;
  const peakPool = shouldHighlight
    ? mappedPoints.filter((p) => p.highlighted)
    : mappedPoints;
  const peakSource = peakPool.length ? peakPool : mappedPoints;
  for (let i = 0; i < peakSource.length; i++) {
    if (peakSource[i].hr_value > peakMaxBpm) {
      peakMaxBpm = peakSource[i].hr_value;
      peakIndex = i;
    }
  }
  const maxPoint = peakSource[peakIndex] || { x: plotLeft + plotWidth, y: plotTop };

  let xTicks = [];
  if (domain?.useWallClock && Number.isFinite(domain.startMs) && Number.isFinite(domain.endMs)) {
    const spanMin = (domain.endMs - domain.startMs) / 60000;
    const count = spanMin < 12 ? 3 : tickCount;
    xTicks = buildWallClockTimeTicks(domain.startMs, domain.endMs, count).map((tick) => ({
      x: plotLeft + tick.ratio * plotWidth,
      label: formatClockTick(tick.ms, localeCode, { withSeconds: spanMin < 8 }),
      ms: tick.ms,
    }));
  } else {
    const count = tMax - tMin < 720 ? 3 : 5;
    for (let i = 0; i < count; i++) {
      const ratio = i / (count - 1);
      const sec = tMin + (tMax - tMin) * ratio;
      const mm = Math.floor(sec / 60);
      const ss = Math.round(sec % 60);
      xTicks.push({
        x: plotLeft + ratio * plotWidth,
        label: `${mm}:${String(ss).padStart(2, "0")}`,
        ms: NaN,
      });
    }
  }

  return {
    areaPath,
    linePath,
    highlightLinePaths,
    maxPoint,
    xTicks,
    yTicks,
    highlightBands,
    plotLeft,
    plotWidth,
    viewWidth,
    viewHeight,
    plotTop,
    plotBottom,
    plotHeight,
  };
}
